/**
 * SalesFunnelChannelRoiSummaryPage — Issue #397.
 *
 * 销售漏斗 + 渠道 ROI 汇总：read-model 视图。
 *
 * 约束：
 *   - 所有 JSON 数据通过 @jinzhuan/api-client；禁止前端假状态。
 *   - 渠道投入 / 渠道分成 / 人员提成 / ROI / 收款金额仅在 reveal 权限存在时展示。
 *   - 未接入数据源展示受控空状态标签，不展示 0 或假数值。
 *   - 导出走带 Authorization 的原生 fetch + response.ok / content-type 校验；后端
 *     侧写入审计 + 水印。前端不直接 <a href> 访问受保护接口。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  canSeeSalesFunnelChannelRoiSummary,
  canGenerateSalesFunnelChannelRoiSummary,
  canReviewSalesFunnelChannelRoiSummary,
  canPublishSalesFunnelChannelRoiSummary,
  canExportSalesFunnelChannelRoiSummary,
  canRevealSalesFunnelChannelRoiSummary,
} from '@/lib/reportsPermissions';
import { getStoredAccessToken } from '@/lib/tokenStorage';
import {
  useSalesFunnelChannelRoiSummaryBatches,
  useSalesFunnelChannelRoiSummaryBatchDetail,
  useSalesFunnelChannelRoiSummaryActions,
  coerceBatchStatus,
  type SalesFunnelChannelRoiSummaryBatchSummaryDto,
  type SalesFunnelChannelRoiSummaryBatchDetailDto,
  type DetailQuery,
} from '@/hooks/useSalesFunnelChannelRoiSummary';

// ─── Status labels ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  generated: '已生成',
  finance_reviewed: '财务已复核',
  published: '已发布',
  archived: '已归档',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  generated: 'bg-sky-100 text-sky-700',
  finance_reviewed: 'bg-indigo-100 text-indigo-700',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-200 text-gray-500',
};

const SOURCE_STATUS_LABELS: Record<string, string> = {
  connected: '已连接',
  partial: '部分接入',
  pending_source: '待数据源接入',
  not_connected: '未接入',
};

const SOURCE_STATUS_COLORS: Record<string, string> = {
  connected: 'text-green-700',
  partial: 'text-amber-600',
  pending_source: 'text-amber-600',
  not_connected: 'text-gray-400',
};

const BATCH_SOURCE_STATUS_LABELS: Record<string, string> = {
  all_connected: '全部数据源已连接',
  partial: '部分数据源已连接',
  pending: '待数据源接入',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  natural_visit: '自然到访',
  referral: '转介绍',
  channel_partner: '渠道商',
  repurchase: '老客户复购',
  marketing_event: '活动会销',
  online_lead: '线上线索',
  unknown: '未归集',
};

// ─── Authenticated export ─────────────────────────────────────────────────────

type ExportFormat = 'csv' | 'xlsx';

const EXPORT_MIME: Record<ExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

async function downloadReport(args: {
  batchId: string;
  reportPeriod: string;
  format: ExportFormat;
  filter: DetailQuery;
}): Promise<void> {
  const token = getStoredAccessToken();
  if (!token) {
    throw new Error('未登录或会话已失效，请重新登录后再导出');
  }
  const expectedMime = EXPORT_MIME[args.format];
  const qs = new URLSearchParams();
  qs.set('format', args.format);
  qs.set('reason', '手动导出');
  if (args.filter.channelPartnerId)
    qs.set('channelPartnerId', args.filter.channelPartnerId);
  if (args.filter.marketTeamId) qs.set('marketTeamId', args.filter.marketTeamId);
  if (args.filter.sourceType) qs.set('sourceType', args.filter.sourceType);
  const url = `/api/v1/reports/sales-funnel-channel-roi-summary/${encodeURIComponent(args.batchId)}/export?${qs.toString()}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: expectedMime,
      },
      credentials: 'include',
    });
  } catch (err) {
    throw new Error(
      `网络错误，导出失败：${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!response.ok) {
    if (response.status === 401)
      throw new Error('会话已失效，请重新登录后再导出');
    if (response.status === 403)
      throw new Error('当前账号没有导出销售漏斗 / 渠道 ROI 汇总的权限');
    if (response.status === 404) throw new Error('报表不存在或已删除');
    throw new Error(`导出失败（HTTP ${response.status}）`);
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes(expectedMime)) {
    throw new Error(
      `导出格式异常：服务器返回 ${contentType || '未知'}，预期 ${expectedMime}`,
    );
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    const filename = `sales_funnel_channel_roi_summary_${args.reportPeriod}.${args.format}`;
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function SourceStatusTag({ status }: { status: string }) {
  return (
    <span
      className={`text-xs font-medium ${SOURCE_STATUS_COLORS[status] ?? 'text-gray-400'}`}
    >
      [{SOURCE_STATUS_LABELS[status] ?? status}]
    </span>
  );
}

function RestrictedCell() {
  return <span className="text-xs italic text-gray-400">[无权限]</span>;
}

function DecimalCell({
  value,
  status,
  canReveal,
}: {
  value: string;
  status?: string;
  canReveal: boolean;
}) {
  if (!canReveal) return <RestrictedCell />;
  if (status === 'not_connected' || status === 'pending_source') {
    return <SourceStatusTag status={status} />;
  }
  return <span className="tabular-nums">{value}</span>;
}

function FunnelSection({
  rows,
  canReveal,
}: {
  rows: SalesFunnelChannelRoiSummaryBatchDetailDto['funnelRows'];
  canReveal: boolean;
}) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-md border border-gray-100 p-4 text-sm text-gray-500">
        本期暂无漏斗数据
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {[
              '阶段',
              '客户数',
              '订单数',
              '订单总金额',
              '收款总金额',
              '本期进入数',
              '本期推进数',
              '数据源状态',
            ].map((h) => (
              <th
                key={h}
                className="px-3 py-2 text-left text-xs font-medium text-gray-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium text-gray-700">
                {r.stageLabel}
                <span className="ml-1 text-xs text-gray-400">
                  ({r.stageKey})
                </span>
              </td>
              <td className="px-3 py-2 tabular-nums">{r.customerCount}</td>
              <td className="px-3 py-2 tabular-nums">{r.orderCount}</td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.orderAmountTotal}
                  status={r.sourceStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.paidAmountTotal}
                  status={r.sourceStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2 tabular-nums">{r.enteredCount}</td>
              <td className="px-3 py-2 tabular-nums">{r.advancedCount}</td>
              <td className="px-3 py-2">
                <SourceStatusTag status={r.sourceStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChannelSection({
  rows,
  canReveal,
}: {
  rows: SalesFunnelChannelRoiSummaryBatchDetailDto['channelRows'];
  canReveal: boolean;
}) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-md border border-gray-100 p-4 text-sm text-gray-500">
        本期暂无渠道 ROI 数据
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {[
              '来源',
              '渠道',
              '团队',
              '客户数',
              '新订单',
              '已成交',
              '服务项目',
              '订单金额',
              '收款金额',
              '渠道投入',
              '渠道分成（待）',
              '渠道分成（已付）',
              '人员提成',
              'ROI',
              '转化率',
              '数据源状态',
            ].map((h) => (
              <th
                key={h}
                className="px-3 py-2 text-left text-xs font-medium text-gray-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-3 py-2">
                {SOURCE_TYPE_LABELS[r.sourceType] ?? r.sourceType}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">
                {r.channelName ?? r.channelPartnerId ?? '-'}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">
                {r.marketTeamName ?? r.marketTeamId ?? '-'}
              </td>
              <td className="px-3 py-2 tabular-nums">{r.customerCount}</td>
              <td className="px-3 py-2 tabular-nums">{r.newOrderCount}</td>
              <td className="px-3 py-2 tabular-nums">
                {r.convertedOrderCount}
              </td>
              <td className="px-3 py-2 tabular-nums">{r.serviceItemCount}</td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.orderAmountTotal}
                  status={r.orderAmountStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.paidAmountTotal}
                  status={r.paidAmountStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.investmentAmount}
                  status={r.investmentStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.channelCommissionPending}
                  status={r.channelCommissionStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.channelCommissionPaid}
                  status={r.channelCommissionStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.staffCommissionAmount}
                  status={r.staffCommissionStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.roiRatio}
                  status={r.investmentStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <DecimalCell
                  value={r.conversionRate}
                  status={r.orderAmountStatus}
                  canReveal={canReveal}
                />
              </td>
              <td className="px-3 py-2">
                <SourceStatusTag status={r.sourceStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GenerateForm({
  onGenerate,
  loading,
}: {
  onGenerate: (period: string, notes?: string) => void;
  loading: boolean;
}) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [notes, setNotes] = useState('');
  const period = `${year}-${String(month).padStart(2, '0')}`;
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-dashed border-gray-300 p-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          年份
        </label>
        <input
          type="number"
          min={2020}
          max={2100}
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
          className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          月份
        </label>
        <select
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value, 10))}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {m} 月
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1">
        <label className="mb-1 block text-xs font-medium text-gray-600">
          备注（可选）
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="本次生成说明"
          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
      </div>
      <button
        type="button"
        onClick={() => onGenerate(period, notes || undefined)}
        disabled={loading}
        className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {loading ? '生成中...' : `生成 ${period} 报表`}
      </button>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function SalesFunnelChannelRoiSummaryPage() {
  const { permissions } = useAuth();
  const granted = permissions ?? [];

  const canRead = canSeeSalesFunnelChannelRoiSummary(granted);
  const canGenerate = canGenerateSalesFunnelChannelRoiSummary(granted);
  const canReview = canReviewSalesFunnelChannelRoiSummary(granted);
  const canPublish = canPublishSalesFunnelChannelRoiSummary(granted);
  const canExport = canExportSalesFunnelChannelRoiSummary(granted);
  const canReveal = canRevealSalesFunnelChannelRoiSummary(granted);

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    fetchList,
  } = useSalesFunnelChannelRoiSummaryBatches();
  const {
    data: detail,
    loading: detailLoading,
    error: detailError,
    fetchDetail,
  } = useSalesFunnelChannelRoiSummaryBatchDetail();
  const actions = useSalesFunnelChannelRoiSummaryActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);

  // Filter inputs (channel / team / source).
  const [channelPartnerId, setChannelPartnerId] = useState('');
  const [marketTeamId, setMarketTeamId] = useState('');
  const [sourceType, setSourceType] = useState('');

  const detailFilter: DetailQuery = useMemo(
    () => ({
      channelPartnerId: channelPartnerId || undefined,
      marketTeamId: marketTeamId || undefined,
      sourceType: sourceType || undefined,
    }),
    [channelPartnerId, marketTeamId, sourceType],
  );

  useEffect(() => {
    if (canRead) {
      void fetchList({ status: coerceBatchStatus(statusFilter) });
    }
  }, [fetchList, statusFilter, canRead]);

  useEffect(() => {
    if (selectedId) {
      void fetchDetail(selectedId, detailFilter);
    }
  }, [fetchDetail, selectedId, detailFilter]);

  const batches = useMemo(() => listData?.data ?? [], [listData]);

  const handleGenerate = async (period: string, notes?: string) => {
    setActionMessage(null);
    const result = await actions.generate({ reportPeriod: period, notes });
    if (result) {
      setActionMessage(
        `报表 ${result.reportPeriod} 已生成（状态：${
          STATUS_LABELS[result.status] ?? result.status
        }）`,
      );
      void fetchList({ status: coerceBatchStatus(statusFilter) });
      setShowGenerate(false);
    } else if (actions.error) {
      setActionMessage(`生成失败：${actions.error.message}`);
    }
  };

  const handleReview = async (id: string, version: number) => {
    setActionMessage(null);
    const result = await actions.review(id, { expectedVersion: version });
    if (result) {
      setActionMessage('财务复核完成');
      if (selectedId === id) void fetchDetail(id, detailFilter);
      void fetchList({ status: coerceBatchStatus(statusFilter) });
    } else if (actions.error) {
      setActionMessage(`复核失败：${actions.error.message}`);
    }
  };

  const handlePublish = async (id: string, version: number) => {
    setActionMessage(null);
    const result = await actions.publish(id, { expectedVersion: version });
    if (result) {
      setActionMessage('报表已发布');
      if (selectedId === id) void fetchDetail(id, detailFilter);
      void fetchList({ status: coerceBatchStatus(statusFilter) });
    } else if (actions.error) {
      setActionMessage(`发布失败：${actions.error.message}`);
    }
  };

  const handleExport = async (
    b: SalesFunnelChannelRoiSummaryBatchSummaryDto,
    format: ExportFormat,
  ) => {
    setActionMessage(null);
    setExportingId(`${b.id}:${format}`);
    try {
      await downloadReport({
        batchId: b.id,
        reportPeriod: b.reportPeriod,
        format,
        filter: detailFilter,
      });
      setActionMessage(`报表 ${b.reportPeriod} 已导出（${format.toUpperCase()}）`);
    } catch (err) {
      setActionMessage(
        `导出失败：${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setExportingId(null);
    }
  };

  if (!canRead) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        您没有查看销售漏斗 / 渠道 ROI 汇总的权限（需要 reports.sales_funnel_channel_roi_summary:read）。
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            销售漏斗 / 渠道 ROI 汇总
          </h1>
          <p className="text-xs text-gray-500">
            按客户来源、阶段转化、渠道投入、订单成交与回款、提成 / 结算状态、订单与服务项目数汇总的 read-model。所有金额为 Decimal-safe 字符串。
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canGenerate && (
            <button
              type="button"
              onClick={() => setShowGenerate((v) => !v)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50"
            >
              {showGenerate ? '收起' : '+ 生成报表'}
            </button>
          )}
        </div>
      </div>

      {actionMessage && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {actionMessage}
        </div>
      )}

      {showGenerate && canGenerate && (
        <GenerateForm onGenerate={handleGenerate} loading={actions.loading} />
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            状态
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">全部状态</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            渠道短码 (channel_partner_id)
          </label>
          <input
            type="text"
            value={channelPartnerId}
            onChange={(e) => setChannelPartnerId(e.target.value)}
            placeholder="例：cp-001"
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            市场团队短码
          </label>
          <input
            type="text"
            value={marketTeamId}
            onChange={(e) => setMarketTeamId(e.target.value)}
            placeholder="例：team-001"
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            来源类型
          </label>
          <select
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">全部来源</option>
            {Object.entries(SOURCE_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Batch list */}
      {listLoading && <div className="text-sm text-gray-500">加载中...</div>}
      {listError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          加载失败：{listError.message}
        </div>
      )}
      {!listLoading && batches.length === 0 && (
        <div className="rounded-md border border-gray-100 p-6 text-center text-sm text-gray-500">
          {statusFilter ? '没有符合条件的报表' : '尚未生成任何报表，点击"生成报表"开始'}
        </div>
      )}
      {batches.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['报告期', '模板版本', '状态', '数据源状态', '生成时间', '操作'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {batches.map((b: SalesFunnelChannelRoiSummaryBatchSummaryDto) => (
                <tr
                  key={b.id}
                  className={`cursor-pointer hover:bg-gray-50 ${
                    selectedId === b.id ? 'bg-sky-50' : ''
                  }`}
                  onClick={() =>
                    setSelectedId(selectedId === b.id ? null : b.id)
                  }
                >
                  <td className="px-3 py-2 font-medium">{b.reportPeriod}</td>
                  <td className="px-3 py-2 text-gray-500">
                    {b.templateVersion}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={b.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {BATCH_SOURCE_STATUS_LABELS[b.sourceStatus] ??
                      b.sourceStatus}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {b.generatedAt
                      ? new Date(b.generatedAt).toLocaleString('zh-CN')
                      : '-'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {canExport && (
                        <>
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                            disabled={exportingId === `${b.id}:csv`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleExport(b, 'csv');
                            }}
                          >
                            {exportingId === `${b.id}:csv`
                              ? '导出中...'
                              : '导出 CSV'}
                          </button>
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                            disabled={exportingId === `${b.id}:xlsx`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleExport(b, 'xlsx');
                            }}
                          >
                            {exportingId === `${b.id}:xlsx`
                              ? '导出中...'
                              : '导出 Excel'}
                          </button>
                        </>
                      )}
                      {canReview && b.status === 'generated' && (
                        <button
                          type="button"
                          className="text-xs text-indigo-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleReview(b.id, b.recordVersion);
                          }}
                        >
                          财务复核
                        </button>
                      )}
                      {canPublish && b.status === 'finance_reviewed' && (
                        <button
                          type="button"
                          className="text-xs text-green-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handlePublish(b.id, b.recordVersion);
                          }}
                        >
                          发布
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selectedId && (
        <div className="space-y-6 rounded-md border border-gray-200 p-4">
          {detailLoading && (
            <div className="text-sm text-gray-500">加载详情...</div>
          )}
          {detailError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              加载详情失败：{detailError.message}
            </div>
          )}
          {detail && detail.id === selectedId && (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">
                  {detail.reportPeriod} 报表详情
                </h2>
                <StatusBadge status={detail.status} />
              </div>

              {!canReveal && (
                <div className="rounded-md border border-amber-100 bg-amber-50 p-2 text-xs text-amber-700">
                  部分列（订单金额、收款金额、渠道投入、提成、ROI）需要 reveal 权限方可查看。
                </div>
              )}

              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  销售漏斗
                </h3>
                <FunnelSection
                  rows={detail.funnelRows}
                  canReveal={canReveal}
                />
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  渠道 ROI
                </h3>
                <ChannelSection
                  rows={detail.channelRows}
                  canReveal={canReveal}
                />
              </section>
            </>
          )}
        </div>
      )}
    </div>
  );
}
